import {DateTime} from 'luxon';

import {UnreachableError} from '../../libs/shared/helpers/src/types/customErrors';
import {makeEnumTypeGuard} from '../../libs/shared/helpers/src/typescript/enumHelpers';
import {CardModel} from '../models/api/cardModel';
import {CustomFieldAttribute} from '../models/api/customFieldAttributeModel';
import {
  CustomFieldModel,
  CustomFieldResourceTypesEnum,
  CustomFieldsEnum,
} from '../models/api/customFieldModel';
import {VariableInterpolationMap} from '../services/variableInterpolation/variableInterpolator';
import {ResourceMap} from '../store/appTypes';
import {Localizer} from '../store/stateless/internationalizationSelectors';
import {formatDateTime, TimeFormatsEnum} from './dateTimeHelpers';

/*
 * Constants.
 */

// e.g. '12/15/20, 6:00 PM PST' or '12/15/20, 18:00 PST' depending on timeFormat.
const customFieldDateTimeFormatOptions: Intl.DateTimeFormatOptions = {
  year: '2-digit',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  timeZoneName: 'short',
};

export const isCustomFieldEnumType = makeEnumTypeGuard(CustomFieldsEnum);

// TODO: Document what this is.
export const customFieldTypes: ReadonlyArray<CustomFieldsEnum> = [
  CustomFieldsEnum.DATETIME,
  CustomFieldsEnum.NUMBER,
  CustomFieldsEnum.TEAMMATE,
  CustomFieldsEnum.STRING,
  CustomFieldsEnum.BOOLEAN,
  CustomFieldsEnum.INBOX,
];

export const maxNumberCustomFields = 50;

/*
 * Exports.
 */

/** The custom field values for variable interpolation are drawn from the various cards
 *  for a recipient, and which card is used is dependent on the draftNamespace.
 *
 *  Currently this means the namespace of the FROM recipient is used (e.g. private channel gets private card values)
 *  and the global card values, if any.
 */
export function buildVariablesFromRecipientCustomFields(
  recipientCard: CardModel | undefined,
  customFields: ResourceMap<CustomFieldModel>,
  localizer: Localizer,
  timeFormat: TimeFormatsEnum,
): VariableInterpolationMap {
  if (!customFields) {
    return {};
  }

  const customFieldMap: VariableInterpolationMap = {};

  const recipientCustomFieldValues = recipientCard?.customFieldAttributes || [];

  // TODO: Use `interpolateCustomAttributes`.

  recipientCustomFieldValues.forEach((customFieldAttribute: CustomFieldAttribute) => {
    const customFieldModel: CustomFieldModel | undefined = customFields[customFieldAttribute.id];
    if (!customFieldModel) {
      return;
    }

    const interpolatedValue = renderCustomFieldDisplayValue(
      customFieldModel,
      customFieldAttribute.value,
      localizer,
      timeFormat,
    );

    customFieldMap[`recipient.${customFieldModel.nameSlug}`] = interpolatedValue;

    customFieldMap[makeVariableSlugFromCustomField(customFieldModel)] = interpolatedValue;
  });

  return customFieldMap;
}

export function interpolateCustomAttributes(
  customFields: ResourceMap<CustomFieldModel>,
  customAttributes: ReadonlyArray<CustomFieldAttribute> | undefined,
  localizer: Localizer,
  timeFormat: TimeFormatsEnum,
) {
  const variablesMap: VariableInterpolationMap = {};

  if (!customAttributes?.length) {
    return variablesMap;
  }

  for (const attribute of customAttributes) {
    const customField = customFields[attribute.id];
    if (!customField) {
      continue;
    }

    const interpolatedValue = renderCustomFieldDisplayValue(
      customField,
      attribute.value,
      localizer,
      timeFormat,
    );

    // Legacy recipent variables support.
    if (customField.resourceType === CustomFieldResourceTypesEnum.CARD) {
      variablesMap[`recipient.${customField.nameSlug}`] = interpolatedValue;
    }

    variablesMap[makeVariableSlugFromCustomField(customField)] = interpolatedValue;
  }

  return variablesMap;
}

/*
 * Helpers.
 */

function renderCustomFieldDisplayValue(
  customFieldModel: CustomFieldModel,
  customFieldValue: string,
  localizer: Localizer,
  timeFormat: TimeFormatsEnum,
): string | undefined {
  if (customFieldModel.type === CustomFieldsEnum.DATETIME) {
    return renderCustomFieldDateTimeValue(customFieldValue, localizer, timeFormat);
  }

  return customFieldValue;
}

function renderCustomFieldDateTimeValue(
  unixEpochMillisAsString: string,
  localizer: Localizer,
  timeFormat: TimeFormatsEnum,
): string | undefined {
  const unixEpochMillis: number = Number(unixEpochMillisAsString);
  if (!isValidUnixEpochTimestamp(unixEpochMillis)) {
    return undefined;
  }

  const dateTime = DateTime.fromMillis(unixEpochMillis);
  return formatDateTime(localizer, dateTime, customFieldDateTimeFormatOptions, timeFormat);
}

function isValidUnixEpochTimestamp(unixEpochMillis: number) {
  return unixEpochMillis >= 0;
}

export function resourceTypeToRootField(resourceType: CustomFieldResourceTypesEnum) {
  switch (resourceType) {
    case CustomFieldResourceTypesEnum.TEAMMATE:
      return 'user';

    case CustomFieldResourceTypesEnum.INBOX:
      return 'inbox';

    case CustomFieldResourceTypesEnum.CARD:
      return 'recipient';

    case CustomFieldResourceTypesEnum.ACCOUNT:
      return 'account';

    default:
      // Never happening since we only fetch specific custom fields.
      throw new UnreachableError(resourceType);
  }
}

export function makeVariableSlugFromCustomField(customField: CustomFieldModel) {
  return `${resourceTypeToRootField(customField.resourceType)}.custom.${customField.nameSlug}`;
}
